<template>
  <div class="o-subscriptions-listing">
    <div class="m-pageHeader">
      <div class="m-pageHeader__title -subscriptions-listing text-pl-black text-body2">Subscriptions</div>
      <div class="m-pageHeader__actions">
        <q-toggle
          class="m-pageHeader__actions__action -showDisabled"
          v-model="showDisabledSubscriptions"
          label="Show Disabled Subscriptions"
          left-label
        />
        <q-btn
          class="m-pageHeader__actions__action -generate"
          label="Generate"
          outline
          color="pl-navy-900"
          @click="generateSubscription"/>
      </div>
    </div>
    <div class="m-pageContent">
      <q-table
        class="o-subscriptions-listing__table"
        :rows="subscriptions"
        :columns="columns"
        :visible-columns="visibleColumns"
        :grid="screen.lt.md"
        binary-state-sort
        row-key="subscription-id"
        :pagination="{
          sortBy: 'customerName',
          descending: false,
          page: 1,
          rowsPerPage: 20
        }"
      >
      <template v-slot:body-cell-enabled="props">
        <td class="o=subscriptions-listing__table__row -enabled">
          <q-checkbox
            :modelValue="isEnabled(props.row)"
            color="pl-navy-900"
            size="xs"
            disable/>
        </td>
      </template>
      <template v-slot:body-cell-actions="props">
        <td class="o-subscriptions-listing__table__row__actions">
          <q-btn
          class="o-subscriptions-listing__table__row__actions__action -view"
          label="View"
          outline
          color="pl-navy-900"
          @click="viewSubscription(props.row)"
          />
          <q-btn
            class="o-subscriptions-listing__table__row__actions__action -disable"
            v-if="isEnabled(props.row)"
            label="Disable"
            outline
            color="pl-navy-900"
            @click="confirmDisableSubscription(props.row)"
          />
          <q-btn
            class="o-subscriptions-listing__table__row__actions__action -enable"
            v-else
            label="Enable"
            outline
            color="pl-navy-900"
            @click="enableSubscription(props.row)"
          />
        </td>
      </template>
      </q-table>
    </div>
    <DiagnosticsDialog v-model="isDiagnosticsOpen"/>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, ref } from 'vue';
import { QTableColumn } from '@/components/utils/qTable';
import { useRouter } from 'vue-router';
import { ISubscriptionListingModel, ISubscriptionService } from '@/store/contracts/subscriptions/subscriptions';
import { SubscriptionRoutes } from '@/router/routes/subscriptions';
import { getServiceName } from '@/store/contracts/subscriptions/subscriptionServiceType';
import { useQuasar } from 'quasar';
import { useStore as useSubscriptionsStore } from '@/store/subscriptions/subscriptions';
import { shortDateFormat } from '@/utils/dateUtil';
import DiagnosticsDialog from '@/components/subscriptions/DiagnosticsDialog.vue';

export default defineComponent({
  components: {
    DiagnosticsDialog
  },
  async setup () {
    const subscriptionsStore = useSubscriptionsStore();
    const router = useRouter();
    const $q = useQuasar();

    const keysPressed = { m: false };
    function onKeyDown (e: KeyboardEvent) {
      keysPressed[e.key] = true;
      if (keysPressed.m && e.ctrlKey && e.altKey && Object.keys(keysPressed).length === 3) {
        toggleDiagnostics();
      }
    }
    onMounted(() => {
      window.addEventListener('keydown', (e) => onKeyDown(e));
      window.addEventListener('keyup', (e) => delete keysPressed[e.key]);
    });
    onUnmounted(() => {
      window.removeEventListener('keydown', (e) => onKeyDown(e));
      window.removeEventListener('keyup', (e) => delete keysPressed[e.key]);
    });

    function displayServices (services: ISubscriptionService[]) {
      if (services.length === 0) {
        return 'None';
      }
      return services.map(s => getServiceName(s.serviceType)).join(', ');
    }
    const subscriptions = computed(() => subscriptionsStore.state.subscriptions?.filter(sub => showDisabledSubscriptions.value || subscriptionsStore.isEnabled(sub)));

    const showDisabledSubscriptions = computed({
      get: () => subscriptionsStore.state.showDisabled,
      set: (value) => subscriptionsStore.setShowDisabled(value)
    });

    enum ColumnNames {
      label = 'label',
      machineId = 'machineId',
      services = 'services',
      activeDate = 'activeDate',
      expiresDate = 'expiresDate',
      enabled = 'enabled',
      actions = 'actions'
    }

    const visibleColumns = computed(() => Object.keys(ColumnNames).filter((c) => showDisabledSubscriptions.value || c !== ColumnNames.enabled));

    const columns: QTableColumn[] = [
      {
        name: ColumnNames.enabled,
        label: 'Enabled',
        align: 'left',
        field: (row: ISubscriptionListingModel) => subscriptionsStore.isEnabled(row)
      },
      {
        name: ColumnNames.label,
        label: 'Label',
        align: 'left',
        field: (row: ISubscriptionListingModel) => row.label,
        sortable: true
      },
      {
        name: ColumnNames.machineId,
        label: 'Machine ID',
        align: 'left',
        field: (row: ISubscriptionListingModel) => row.machineId
      },
      {
        name: ColumnNames.services,
        label: 'Services',
        align: 'left',
        field: (row: ISubscriptionListingModel) => displayServices(row.services)
      },
      {
        name: ColumnNames.activeDate,
        label: 'Activation Date',
        align: 'left',
        field: (row: ISubscriptionListingModel) => shortDateFormat(row.activeDate),
        sortable: true,
        sort: (a, b) => new Date(a).getTime() - new Date(b).getTime()
      },
      {
        name: ColumnNames.expiresDate,
        label: 'Expiration Date',
        align: 'left',
        field: (row: ISubscriptionListingModel) => shortDateFormat(row.expiresDate),
        sortable: true,
        sort: (a, b) => new Date(a).getTime() - new Date(b).getTime()
      },
      {
        name: ColumnNames.actions,
        label: '',
        align: 'left',
        field: (row: ISubscriptionListingModel) => row
      }
    ];

    await subscriptionsStore.loadSubscriptions();

    function viewSubscription (subscription: ISubscriptionListingModel) {
      router.push({ name: SubscriptionRoutes.view, params: { subscriptionId: subscription.subscriptionId } });
    }

    function generateSubscription () {
      router.push({ name: SubscriptionRoutes.generate });
    }

    function confirmDisableSubscription (subscription: ISubscriptionListingModel) {
      $q.dialog({
        title: 'Disable Subscription Confirmation',
        message: 'Are you sure you want to disable this subscription?',
        cancel: true,
        persistent: true
      }).onOk(() => {
        subscriptionsStore.disableSubscription(subscription.subscriptionId);
      });
    }

    async function enableSubscription (subscription: ISubscriptionListingModel) {
      await subscriptionsStore.enableSubscription(subscription.subscriptionId);
    }

    const isDiagnosticsOpen = ref(false);
    function toggleDiagnostics () {
      isDiagnosticsOpen.value = !isDiagnosticsOpen.value;
    }

    return {
      columns,
      visibleColumns,
      subscriptions,
      screen: $q.screen,
      viewSubscription,
      generateSubscription,
      isEnabled: subscriptionsStore.isEnabled,
      confirmDisableSubscription,
      enableSubscription,
      showDisabledSubscriptions,
      isDiagnosticsOpen
    };
  }
});
</script>

<style lang="scss">
.o-subscriptions-listing {
    margin: 20px;

  .m-pageHeader {
      justify-content: space-between;
      display: flex;
      align-items: center;
      margin-bottom: 24px;
  }

  &__table {

    .q-table__top.relative-position.row.items-center {
      justify-content: space-between;
    }

    .q-table__bottom.row.items-center.justify-end {
      justify-content: flex-start;
      padding-left: 8px;

      .q-table__separator {
        flex: 0 0 auto;
      }
    }

    &__row {
      &__actions {
        display: flex;
        justify-content: flex-end;
        align-items: left;
        padding: 8px;

        &__action+&__action {
          margin-left: 8px;
          width: 90px;
        }
      }
    }
  }
}
</style>
